import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

export interface GmailConnection {
  gmail_email: string;
  status: 'active' | 'expired' | 'revoked';
  connected_at: string;
  last_sync_at: string | null;
}

export interface GmailMessageSummary {
  id: string;
  thread_id: string | null;
  subject: string;
  from: string;
  snippet: string;
  received_at: string | null;
  is_read: boolean;
}

export interface GmailMessageDetail extends GmailMessageSummary {
  to: string[];
  cc: string[];
  body_html: string | null;
  body_text: string | null;
}

export interface GmailMessagesResponse {
  messages: GmailMessageSummary[];
  nextPageToken: string | null;
}

export interface SendGmailInput {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
}

export interface GmailStatusRow {
  user_id: string;
  gmail_email: string | null;
  status: 'active' | 'expired' | 'revoked' | 'not_connected';
  connected_at: string | null;
  last_sync_at: string | null;
}

export const gmailKeys = {
  all: ['gmail'] as const,
  connection: () => [...gmailKeys.all, 'connection'] as const,
  messages: (pageToken?: string | null) =>
    [...gmailKeys.all, 'messages', pageToken ?? null] as const,
  message: (messageId: string) => [...gmailKeys.all, 'message', messageId] as const,
  statuses: (tenantId: string) => [...gmailKeys.all, 'statuses', tenantId] as const,
} as const;

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export function useGmailConnection(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: gmailKeys.connection(),
    queryFn: () => apiFetch<GmailConnection | null>(supabase, 'gmail/connection'),
  });
}

export interface ConnectGmailInput {
  returnTo?: string;
}

export function useConnectGmail(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: (input?: ConnectGmailInput) => {
      const qs = input?.returnTo ? `?return_to=${encodeURIComponent(input.returnTo)}` : '';
      return apiFetch<{ url: string }>(supabase, `gmail/oauth/start${qs}`);
    },
  });
}

export function useDisconnectGmail(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(supabase, 'gmail/connection', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: gmailKeys.connection() });
      void qc.invalidateQueries({ queryKey: gmailKeys.messages() });
      void qc.invalidateQueries({ queryKey: gmailKeys.statuses('') });
    },
  });
}

export function useGmailMessages(
  supabase: SupabaseClientLike,
  opts: { pageToken?: string | null; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: gmailKeys.messages(opts.pageToken),
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts.pageToken) params.set('pageToken', opts.pageToken);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      return apiFetch<GmailMessagesResponse>(supabase, `gmail/messages${suffix}`);
    },
    enabled: opts.enabled ?? true,
  });
}

export function useGmailMessage(
  supabase: SupabaseClientLike,
  messageId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: gmailKeys.message(messageId ?? ''),
    queryFn: () => apiFetch<GmailMessageDetail>(supabase, `gmail/messages/${messageId}`),
    enabled: Boolean(messageId) && enabled,
  });
}

export function useSendGmail(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendGmailInput) =>
      apiFetch<{ id: string }>(supabase, 'gmail/send', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: gmailKeys.messages() });
    },
  });
}

export function useMarkGmailRead(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiFetch<{ id: string; ok: true }>(supabase, 'gmail/mark-read', {
        method: 'POST',
        body: JSON.stringify({ id: messageId }),
      }),
    onSuccess: (_data, messageId) => {
      void qc.invalidateQueries({ queryKey: gmailKeys.messages() });
      void qc.invalidateQueries({ queryKey: gmailKeys.message(messageId) });
    },
  });
}

export function useGmailStatuses(supabase: SupabaseClientLike, tenantId: string, enabled = true) {
  return useQuery({
    queryKey: gmailKeys.statuses(tenantId),
    queryFn: () => apiFetch<{ data: GmailStatusRow[] }>(supabase, 'gmail/statuses'),
    enabled: Boolean(tenantId) && enabled,
  });
}
